import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Bug, Lightbulb, HelpCircle, ExternalLink } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { issuesApi } from '@/lib/api'

type IssueType = 'bug' | 'feature' | 'question'

interface ReportIssueDialogProps {
  trigger?: React.ReactNode
}

export function ReportIssueDialog({ trigger }: ReportIssueDialogProps) {
  const [open, setOpen] = useState(false)
  const [type, setType] = useState<IssueType>('bug')
  const [success, setSuccess] = useState<{ issueNumber: number; issueUrl: string } | null>(null)
  const [error, setError] = useState<string | null>(null)

  const createIssue = useMutation({
    mutationFn: issuesApi.create,
    onSuccess: (response) => {
      setSuccess({
        issueNumber: response.data.issueNumber,
        issueUrl: response.data.issueUrl
      })
      setError(null)
    },
    onError: (err: any) => {
      setError(err.response?.data?.error || 'Failed to submit issue')
      setSuccess(null)
    }
  })

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const formData = new FormData(e.currentTarget)
    createIssue.mutate({
      title: formData.get('title') as string,
      description: formData.get('description') as string,
      type
    })
  }

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen)
    if (!isOpen) {
      setSuccess(null)
      setError(null)
      setType('bug')
    }
  }

  const typeOptions = [
    { value: 'bug' as const, label: 'Bug Report', icon: Bug, description: 'Something is broken' },
    { value: 'feature' as const, label: 'Feature Request', icon: Lightbulb, description: 'Suggest an improvement' },
    { value: 'question' as const, label: 'Question', icon: HelpCircle, description: 'Ask for help' },
  ]

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {trigger || <Button variant="outline" size="sm"><Bug className="h-4 w-4 mr-2" />Report Issue</Button>}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Report an Issue</DialogTitle>
          <DialogDescription>
            Submit feedback directly to our GitHub repository
          </DialogDescription>
        </DialogHeader>

        {success ? (
          <div className="space-y-4">
            <Alert>
              <AlertDescription>
                Issue #{success.issueNumber} created successfully!
              </AlertDescription>
            </Alert>
            <div className="flex gap-2">
              <Button variant="outline" asChild className="flex-1">
                <a href={success.issueUrl} target="_blank" rel="noopener noreferrer">
                  View on GitHub <ExternalLink className="h-4 w-4 ml-2" />
                </a>
              </Button>
              <Button onClick={() => handleOpenChange(false)} className="flex-1">
                Close
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label>Issue Type</Label>
              <div className="grid grid-cols-3 gap-2">
                {typeOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setType(option.value)}
                    className={`p-3 rounded-lg border text-center transition-colors ${
                      type === option.value
                        ? 'border-primary bg-primary/10'
                        : 'border-border hover:border-primary/50'
                    }`}
                  >
                    <option.icon className="h-5 w-5 mx-auto mb-1" />
                    <div className="text-sm font-medium">{option.label}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                name="title"
                placeholder="Brief summary of the issue"
                required
                minLength={5}
                maxLength={100}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                name="description"
                placeholder={
                  type === 'bug'
                    ? "What happened? What did you expect to happen? Steps to reproduce..."
                    : type === 'feature'
                    ? "Describe the feature you'd like to see..."
                    : "What do you need help with?"
                }
                required
                minLength={20}
                rows={5}
              />
            </div>

            <div className="flex gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button type="submit" disabled={createIssue.isPending} className="flex-1">
                {createIssue.isPending ? 'Submitting...' : 'Submit Issue'}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}
